import { forwardRef, Ref, useCallback, useImperativeHandle, useMemo } from 'react';
import { setDevicesToLedMode } from '@/api/storeManagement/device';
import { MobileFeatureHandle, MobileFeatureViewProps } from '@/mobile/types';
import { createOnScanHandler } from '@/mobile/utils/createOnScanHandler';
import { useMobileState } from '@/mobile/provider/context';
import { normalizeIdentifier } from '@/utils/associateUtils';

const LED_COMMAND = 'led 1 4 100 100 60';

const LedControlView = forwardRef(({ dependency }: MobileFeatureViewProps, ref: Ref<MobileFeatureHandle>) => {
    const { state } = useMobileState();
    const {
        settings,
        refs: { isSendingRef },
        method: { focusCommonEslInput, setESL, setButtonInfo, addHistoryItem },
    } = dependency;

    const handleLedCommand = useCallback(async (finalMac: string) => {
        isSendingRef.current = true;
        const { type, normalized } = normalizeIdentifier(finalMac, settings);
        setESL(normalized);
        setButtonInfo({ type: 'sending' });

        try {
            const {
                data: { data, isSuccess, message },
            } = await setDevicesToLedMode([normalized], LED_COMMAND);

            if (isSuccess) {
                setButtonInfo({ type: 'success' });
                addHistoryItem({
                    eslMac: normalized,
                    status: 'success',
                    timestamp: new Date().toLocaleString(),
                    special_job_text: 'led-control',
                    other_info: type,
                });
                return;
            }

            const reason = data?.reason || message || 'LED command failed';
            throw new Error(reason);
        } catch (error) {
            const errorReason = (error as Error).message || 'Unknown error occurred';
            setButtonInfo({ type: 'error', msg: errorReason });
            addHistoryItem({
                eslMac: normalized,
                status: 'failed',
                timestamp: new Date().toLocaleString(),
                special_job_text: 'led-control',
                other_info: type,
                errorReason,
            });
        } finally {
            isSendingRef.current = false;
            setESL('');
        }
    }, []);

    const onEslEnter = useCallback((value: string) => handleLedCommand(value), []);

    const onScan = useMemo(
        () =>
            createOnScanHandler({
                shouldProcess: () => !state.isSubmitting,
                onNormalizedScan: handleLedCommand,
            }),
        [state.isSubmitting, onEslEnter],
    );

    const onModeChange = useCallback(() => {
        focusCommonEslInput();
    }, []);

    // Implement methods that parent use.
    useImperativeHandle(
        ref,
        () => ({
            onEslEnter,
            onScan,
            onModeChange,
        }),
        [onEslEnter, onScan, onModeChange],
    );

    return null;
});

export default LedControlView;
